import {Collaborator} from 'auth/PermissionConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {EntrySummary} from 'notebook/ELNConstants';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import {RegistrationRecordList, WorkflowSummary} from 'workflows/WorkflowConstants';

export enum ModelsActionType {
  ENTRIES_FETCH_COMPLETED = 'ENTRIES_FETCH_COMPLETED',
  FILES_FETCH_COMPLETED = 'FILES_FETCH_COMPLETED',
  FILES_PARTIAL_FETCH_COMPLETED = 'FILES_PARTIAL_FETCH_COMPLETED',
  VALIDATION_EXTRAS_FETCH_COMPLETED = 'VALIDATION_EXTRAS_FETCH_COMPLETED',
  REGISTRATION_RECORD_LISTS_FETCH_COMPLETED = 'REGISTRATION_RECORD_LISTS_FETCH_COMPLETED',
  WORKFLOW_WORKFLOW_SUMMARIES_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_SUMMARIES_FETCH_COMPLETED',
  COLLABORATORS_FETCH_COMPLETED = 'COLLABORATORS_FETCH_COMPLETED',
  STORABLES_FETCH_COMPLETED = 'STORABLES_FETCH_COMPLETED',
  BATCHES_FETCH_COMPLETED = 'BATCHES_FETCH_COMPLETED',
}

export interface EntriesFetchCompletedAction {
  type: ModelsActionType.ENTRIES_FETCH_COMPLETED;
  entrySummariesById: {[id: string]: EntrySummary};
  inaccessibleEntryIds: Array<string>;
}
export function entriesFetchCompleted(
  entrySummariesById: {[id: string]: EntrySummary},
  inaccessibleEntryIds: Array<string> = []
): EntriesFetchCompletedAction {
  return {
    type: ModelsActionType.ENTRIES_FETCH_COMPLETED,
    entrySummariesById,
    inaccessibleEntryIds,
  };
}

export interface FilesFetchCompletedAction {
  type: ModelsActionType.FILES_FETCH_COMPLETED;
  fileSummariesById: {[id: string]: BioentitySummary};
  inaccessibleFileIds: Array<string>;
}
export function filesFetchCompleted(
  fileSummariesById: {[id: string]: BioentitySummary},
  inaccessibleFileIds: Array<string> = []
): FilesFetchCompletedAction {
  return {
    type: ModelsActionType.FILES_FETCH_COMPLETED,
    fileSummariesById,
    inaccessibleFileIds,
  };
}

/**
 * Summaries fetched from an endpoint that intentionally omits some fields (e.g. global search omits
 * tags and non-registry aliases to keep the payload small). These are merged into summaries already
 * in the cache rather than replacing them, and are ignored for entities not yet cached, so the cache
 * never holds a summary that is missing fields consumers expect to be present.
 */
export interface FilesPartialFetchCompletedAction {
  type: ModelsActionType.FILES_PARTIAL_FETCH_COMPLETED;
  partialFileSummariesById: {[id: string]: Partial<BioentitySummary>};
}
export function filesPartialFetchCompleted(partialFileSummariesById: {
  [id: string]: Partial<BioentitySummary>;
}): FilesPartialFetchCompletedAction {
  return {
    type: ModelsActionType.FILES_PARTIAL_FETCH_COMPLETED,
    partialFileSummariesById,
  };
}

export interface ValidationExtrasFetchCompletedAction {
  type: ModelsActionType.VALIDATION_EXTRAS_FETCH_COMPLETED;
  validationExtrasByBioentityId: {[id: string]: ValidationExtras};
  inaccessibleFileIds: Array<string>;
}
export function validationExtrasFetchCompleted(
  validationExtrasByBioentityId: {[id: string]: ValidationExtras},
  inaccessibleFileIds: Array<string> = []
): ValidationExtrasFetchCompletedAction {
  return {
    type: ModelsActionType.VALIDATION_EXTRAS_FETCH_COMPLETED,
    validationExtrasByBioentityId,
    inaccessibleFileIds,
  };
}

export interface RegistrationRecordListsFetchCompletedAction {
  type: ModelsActionType.REGISTRATION_RECORD_LISTS_FETCH_COMPLETED;
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
}
export function registrationRecordListsFetchCompleted(registrationRecordListsById: {
  [id: string]: RegistrationRecordList;
}): RegistrationRecordListsFetchCompletedAction {
  return {
    type: ModelsActionType.REGISTRATION_RECORD_LISTS_FETCH_COMPLETED,
    registrationRecordListsById,
  };
}

export interface WorkflowSummariesFetchCompletedAction {
  type: ModelsActionType.WORKFLOW_WORKFLOW_SUMMARIES_FETCH_COMPLETED;
  workflowSummaries: Array<WorkflowSummary>;
}
export function workflowSummariesFetchCompleted(workflowSummariesById: {
  [id: string]: WorkflowSummary;
}): WorkflowSummariesFetchCompletedAction {
  return {
    type: ModelsActionType.WORKFLOW_WORKFLOW_SUMMARIES_FETCH_COMPLETED,
    workflowSummaries: Object.values(workflowSummariesById),
  };
}

export interface CollaboratorsFetchCompletedAction {
  type: ModelsActionType.COLLABORATORS_FETCH_COMPLETED;
  collaboratorsByOwnableId: {[id: string]: Array<Collaborator>};
}
export function collaboratorsFetchCompleted(collaboratorsByOwnableId: {
  [id: string]: Array<Collaborator>;
}): CollaboratorsFetchCompletedAction {
  return {
    type: ModelsActionType.COLLABORATORS_FETCH_COMPLETED,
    collaboratorsByOwnableId,
  };
}

export interface StorablesFetchCompletedAction {
  type: ModelsActionType.STORABLES_FETCH_COMPLETED;
  inaccessibleStorableIds: Array<string>;
}
export function storablesFetchCompleted(
  inaccessibleStorableIds: Array<string> = []
): StorablesFetchCompletedAction {
  return {
    type: ModelsActionType.STORABLES_FETCH_COMPLETED,
    inaccessibleStorableIds,
  };
}

export interface BatchesFetchCompletedAction {
  type: ModelsActionType.BATCHES_FETCH_COMPLETED;
  inaccessibleBatchIds: Array<string>;
}
export function batchesFetchCompleted(inaccessibleBatchIds: Array<string> = []): BatchesFetchCompletedAction {
  return {
    type: ModelsActionType.BATCHES_FETCH_COMPLETED,
    inaccessibleBatchIds,
  };
}

export type ModelsAction =
  | EntriesFetchCompletedAction
  | FilesFetchCompletedAction
  | FilesPartialFetchCompletedAction
  | ValidationExtrasFetchCompletedAction
  | RegistrationRecordListsFetchCompletedAction
  | WorkflowSummariesFetchCompletedAction
  | CollaboratorsFetchCompletedAction
  | StorablesFetchCompletedAction
  | BatchesFetchCompletedAction;
